import { observer } from 'mobx-react';
import * as React from 'react';
import { classes, cssRaw, style } from 'typestyle';
import { Component, deps, inject } from '../../../lib/component';
import { colorAlmostWhite, colorGreyAccent, colorGunmetal, colorWhite, rebrand } from '../../../theme/color';
import { resetAnchorStyles, resetButton } from '../../../theme/reset';
import { TrackSchema } from '../../../types/schema';
import { ArtistLinkList } from '../../artist-link-list';
import { AddRemoveCompat } from '../../compat/add-remove-compat';
import { FavouriteButtonCompat } from '../../compat/favourite-button-compat';
import { Duration } from '../../duration';
import { CheckmarkCircle } from '../atoms/icons/CheckmarkCircle';
import { StarCircle } from '../atoms/icons/StarCircle';
import { ImageWithPlayBtn } from '../atoms/ImageWithPlayBtn';
import { isEmpty } from '../utilities/objects';
import { Link } from '../atoms/Link';
import { important, px } from 'csx';
import { AddToPlaylistButton } from '../atoms/button/AddToPlaylistButton';
import { UserPlaylistContext } from '../pages/UserPlaylistPage';
import { RemoveFromPlaylistButton } from '../atoms/button/RemoveFromPlaylistButton';
import { TagButton } from '../atoms/button/TagButton';
import { Tooltip } from '../atoms/Tooltip';
import { Variations } from '../atoms/Variations';
import { InfoOutline } from '../atoms/icons/InfoOutline';
import { ExplicitTag } from '../atoms/ExplicitTag';
import { SubscriptionInfoModal } from '../organisms/SubscriptionInfoModal';
import { center, centerCenter, endJustified, flexRoot, start, vertical } from 'csstips';
import { spacing } from '../../../theme/spacing';
import {
  containerTrackline2XS,
  containerTracklineXS,
  containerTracklineSM,
  containerTracklineMD,
  containerTracklineLG,
  containerTracklineXL,
  containerUpToTrackline2XS,
  containerUpToTracklineXS,
  containerUpToTracklineSM,
  containerUpToTracklineMD,
  containerUpToTracklineLG,
  containerUpToTracklineXL,
  isFinePointer,
} from '../utilities/mediaQueries';
import { reaction } from 'mobx';
import { TrackInfoButton } from '../atoms/button/TrackInfoButton';
import { NoVocal } from '../atoms/icons/NoVocalInstrumental';
import { CloseCross } from '../atoms/icons/CloseCross';
import { tooltipStyles } from '../../../styles/tooltipStyles';
import { borderRadius } from '../../../theme/border';

// Container queries aren't included in typestyle, so we have to chuck this in raw CSS
// If you're not familiar with container queries, see https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_containment/Container_queries
const tracklineContainerName = 'trackline';
const tracklineBgCssVar = '--trackline-bg-color';

cssRaw(`
  ${containerUpToTrackline2XS(tracklineContainerName)} {
    .trackline-favourite-btn {
      display: none !important;
    }
  }

  ${containerTrackline2XS(tracklineContainerName)} {
    .trackline {
      grid-template-columns: 56px 1fr 160px !important;
    }
  }

  ${containerUpToTracklineXS(tracklineContainerName)} {
    .trackline-mood {
      display: none !important;
    }
  }

  ${containerTracklineXS(tracklineContainerName)} {
    .trackline {
      grid-template-columns: 56px 1fr 84px 160px !important;
    }
  }

  ${containerUpToTracklineSM(tracklineContainerName)} {
    .trackline-genre,
    .trackline-catalogue-type {
      display: none !important;
    }
  }

  ${containerTracklineSM(tracklineContainerName)} {
    .trackline {
      grid-template-columns: 56px 1fr repeat(2, 84px) 16px 160px !important;
    }

    .trackline-catalogue-small {
      display: none !important;
    }
  }

  ${containerUpToTracklineMD(tracklineContainerName)} {
    .trackline-release {
      display: none !important;
    }
  }

  ${containerTracklineMD(tracklineContainerName)} {
    .trackline {
      grid-template-columns: 56px 232px 1fr repeat(2, 84px) 16px 160px !important;
    }
  }

  ${containerUpToTracklineLG(tracklineContainerName)} {
    .trackline-variations-no-vocals,
    .trackline-duration {
      display: none !important;
    }
  }

  ${containerTracklineLG(tracklineContainerName)} {
    .trackline {
      grid-template-columns: 56px 232px 56px 1fr repeat(2, 84px) 51px 16px 160px !important;
      gap: ${spacing.DEFAULT} !important;
    }

    /* Override column gap for track title */
    .trackline-title-container {
      margin-left: -8px;
    }
  }

  ${containerUpToTracklineXL(tracklineContainerName)} {
    .trackline-catalogue-label {
      display: none !important;
    }
  }

  ${containerTracklineXL(tracklineContainerName)} {
    .trackline {
      grid-template-columns: 56px 232px 56px 1fr repeat(2, 84px) 51px 68px 160px !important;
    }
  }
`);

const styles = {
  tracklineContainer: style({
    containerType: 'inline-size',
    containerName: tracklineContainerName,
  }),
  trackline: style({
    display: 'grid',
    gridTemplateColumns: '56px 1fr 120px',
    gap: px(8),
    maxWidth: '1087px',
    maxHeight: '65px',
    borderBottom: `1px solid ${colorGreyAccent.toString()}`,
    padding: spacing.XXSMALL,
    backgroundColor: `var(${tracklineBgCssVar}, ${colorWhite.toString()})`,
    fontSize: '12px',
    alignItems: 'center',
    $nest: {
      '& a': {
        color: rebrand.dark1.toString(),
      },
      '& a:hover, & a:focus': {
        color: rebrand.dark2.toString(),
        textDecoration: 'underline',
      },
      '&:hover, &:focus-within': {
        [tracklineBgCssVar as any]: colorAlmostWhite.toString(),
      },
    },
  }),
  nowPlaying: style({
    [tracklineBgCssVar as any]: colorAlmostWhite.toString(),
  }),
  trackTitleContainer: style(flexRoot, vertical, start, {
    width: '100%',
    $nest: {
      '& p': {
        margin: '0px',
      },
    },
  }),
  fadeToWhite: style({
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    position: 'relative',
    $nest: {
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        width: '20px',
        height: '100%',
        background: `linear-gradient(to right, transparent, var(${tracklineBgCssVar}, ${colorWhite.toString()}))`,
        pointerEvents: 'none',
      },
    },
  }),
  trackTitle: style({
    display: 'flex',
    alignItems: 'center',
    lineHeight: 1.3,
    fontWeight: 600,
  }),
  planLabel: style(resetButton, flexRoot, center, {
    gap: spacing.XXSMALL,
    color: rebrand.contrast[40].toString(),
    $nest: {
      '&.premium': {
        color: rebrand.primary[100].toHexString(),
      },
    },
  }),
  actionButtons: style(flexRoot, center, endJustified, {
    gap: px(8),
    paddingRight: px(8),
  }),
  addToPlaylistButton: style({
    backgroundColor: colorGreyAccent.toString(),
  }),
  artistLinkList: style({ color: colorGunmetal.toString(), $nest: { ...resetAnchorStyles(colorGunmetal.toString()) } }),
  variations: style(flexRoot, center, {
    gap: px(4),
    color: rebrand.neutralOnLight[80].toString(),
  }),
  noVocals: style({
    marginLeft: 'auto',
  }),
  variationsLink: style({
    display: 'inline-block',
    padding: spacing.XXSMALL,
    borderRadius: borderRadius.SMALL,
    $nest: {
      '&:hover': {
        backgroundColor: colorGreyAccent.toString(),
      },
    },
  }),
  tagButton: style({
    whiteSpace: 'nowrap',
    maxWidth: '84px',
    overflow: 'hidden',
  }),
  toolTipStyles: style({
    textTransform: 'capitalize',
  }),
  customTooltipPosition: style({
    transform: 'translateY(-4px) translateX(-8px) !important',
    $nest: {
      '&::after': {
        left: 'calc(12% - 4px) !important',
      },
    },
  }),
  variationContainer: style(flexRoot, vertical, centerCenter, {
    position: 'relative',
  }),
  unavailableTrackColor: style({
    color: important(rebrand.neutralOnLight[20].toString()),
  }),
  unavailableTrackPlanColor: style({
    color: important(rebrand.primary[20].toString()),
  }),
  boldFont: style({
    fontWeight: 700,
  }),
  variationToolTip: style({
    bottom: px(8),
  }),
  tooltipCloseCross: style({
    padding: `${spacing.XSMALL} ${spacing.XLARGE} ${spacing.XSMALL} ${spacing.SMALL}`,
  }),
};

export interface TrackLineProps {
  track: TrackSchema;
  onClickLink?(event: React.MouseEvent<any>): any;
  onAddTrackToPlaylist?(): void;
  onRemoveTrackFromPlaylist?(): void;
  className?: string;
  mix?: boolean;
}

type State = {
  showSubscriptionLabelInfo: boolean;
  genreTagLoading: boolean;
  moodTagLoading: boolean;
  showUnavailableTooltip: boolean;
  hidePlayBtn: boolean;
};

@observer
@inject(deps)
export class TrackLine extends Component<TrackLineProps> {
  _isMounted = false;

  state: State = {
    showSubscriptionLabelInfo: false,
    genreTagLoading: false,
    moodTagLoading: false,
    showUnavailableTooltip: false,
    hidePlayBtn: true,
  };

  constructor(props) {
    super(props);

    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
  }
  disposer: () => void;

  isSearchPage() {
    return this.props.model.router.location.pathname.startsWith('/search');
  }

  // Mouse enter and leave events should only be triggered if not using a touch device
  handleMouseEnter(): void {
    if (isFinePointer.matches) {
      this.setState({ hidePlayBtn: false });
    }
  }

  handleMouseLeave() {
    if (isFinePointer.matches) {
      this.setState({ hidePlayBtn: true });
    }
  }

  componentDidMount() {
    this._isMounted = true;

    if (this.isSearchPage()) {
      this.disposer = reaction(
        () => this.props.model.router.location.search,
        () => {
          // Set loading state to true when the search query changes
          this.setState({ genreTagLoading: true, moodTagLoading: true });

          // Force an update when the search changes
          this.forceUpdate();

          setTimeout(() => {
            // If the component is still mounted after the update, set the tags loading state to false
            if (this._isMounted) {
              this.setState({ genreTagLoading: false, moodTagLoading: false });
            }
          }, 900);
        }
      );
    }
  }

  componentWillUnmount() {
    // Clean up the reaction when the component unmounts
    if (this.disposer) {
      this.disposer();
    }
    // Disable `this.setState` inside the setTimeout function in componentDidMount
    this._isMounted = false;
  }

  render() {
    const {
      track,
      onClickLink,
      className,
      mix,
      onAddTrackToPlaylist,
      onRemoveTrackFromPlaylist,
      controller: {
        analytics: { sendMixpanel },
        modal: { showPlaylistSubscribeModal },
      },
      model: {
        user: { user, channel },
        page: {
          searchMvp: { moods, genres },
        },
        router: { location },
        player: { currentTrack, play },
      },
    } = this.props;

    const { showSubscriptionLabelInfo, genreTagLoading, moodTagLoading, hidePlayBtn } = this.state;
    const trackIsAvailable = track.is_available_in_territory === null ? true : track.is_available_in_territory;

    const isSearchPage = location.pathname.startsWith('/search');

    type Tag = string | { name: string; type?: string };

    // Create name-to-slug maps
    const genreNameToSlugMap = genres.reduce((acc, genre) => {
      acc[genre.name.toLowerCase()] = genre.slug;
      return acc;
    }, {} as Record<string, string>);

    const moodNameToSlugMap = moods.reduce((acc, mood) => {
      acc[mood.name.toLowerCase()] = mood.slug;
      return acc;
    }, {} as Record<string, string>);

    // Initial genre and mood tags
    const initialGenreTags: Tag[] = track.tags?.musiio_genre || [];
    const initialMoodTags: Tag[] = track.tags?.musiio_mood || [];

    // Map tags to include slugs
    const genreTags = initialGenreTags.map((tag) => {
      let tagName: string;

      if (typeof tag === 'string') {
        tagName = tag;
      } else if (tag && typeof tag.name === 'string') {
        tagName = tag.name;
      } else {
        tagName = '';
      }

      return {
        name: tagName,
        slug: genreNameToSlugMap[tagName.toLowerCase()] || tagName.toLowerCase(),
      };
    });

    const moodTags = initialMoodTags.map((tag) => {
      let tagName: string;

      if (typeof tag === 'string') {
        tagName = tag;
      } else if (tag && typeof tag.name === 'string') {
        tagName = tag.name;
      } else {
        tagName = '';
      }

      return {
        name: tagName,
        slug: moodNameToSlugMap[tagName.toLowerCase()] || tagName.toLowerCase(),
      };
    });

    let allGenreTags = [];
    let allMoodTags = [];

    if (isSearchPage) {
      const urlParams = new URLSearchParams(location.search);

      const searchMoodTags = urlParams.get('mood') ? urlParams.get('mood').split(',') : [];
      const searchGenreTags = urlParams.get('genre') ? urlParams.get('genre').split(',') : [];

      const rearrangedGenreTags = this.rearrangeTags(genreTags, searchGenreTags);
      const rearrangedMoodTags = this.rearrangeTags(moodTags, searchMoodTags);

      allGenreTags = rearrangedGenreTags;
      allMoodTags = rearrangedMoodTags;
    } else {
      allGenreTags = genreTags;
      allMoodTags = moodTags;
    }

    // Extract names and slugs
    const firstThreeGenreTags = allGenreTags
      .slice(0, 3)
      .map((tag) => tag.name)
      .join(', ');
    const firstThreeMoodTags = allMoodTags
      .slice(0, 3)
      .map((tag) => tag.name)
      .join(', ');

    const firstGenreTag = allGenreTags[0] || {};
    const firstMoodTag = allMoodTags[0] || {};

    const firstGenreTagName = firstGenreTag.name || '';
    const firstGenreTagSlug = firstGenreTag.slug || '';

    const firstMoodTagName = firstMoodTag.name || '';
    const firstMoodTagSlug = firstMoodTag.slug || '';

    const unavailableTooltipContent = (
      <span className={tooltipStyles.tooltipInner}>
        <span>Not available in your region</span>
        <button
          className={tooltipStyles.tooltipCloseBtn}
          onClick={() => this.setState({ showUnavailableTooltip: false })}
        >
          <CloseCross color="inherit" size={16} />
        </button>
      </span>
    );

    // Is the track currently playing?
    const isCurrent = currentTrack && track.identity === currentTrack.identity;
    const isCurrentPlaying = play && isCurrent;

    return (
      <div className={styles.tracklineContainer}>
        <div
          className={classes(styles.trackline, 'trackline', isCurrentPlaying && styles.nowPlaying, className)}
          data-test-track-line={track.identity}
          data-test-is_available_in_territory={trackIsAvailable}
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseLeave}
        >
          <SubscriptionInfoModal
            show={showSubscriptionLabelInfo}
            payg={!track.is_stock_music}
            channel={channel}
            onRequestClose={() => this.setState({ showSubscriptionLabelInfo: false })}
          />

          {/* ARTWORK */}
          <ImageWithPlayBtn
            track={track}
            small={true}
            newTrackBadge={true}
            hasImgRadius
            data-test-trackline-artwork
            hidePlayBtn={hidePlayBtn && !isCurrentPlaying}
          />

          {/* TITLE / ARTISTS / SMALL CATALOGUE TYPE */}
          <div
            className={classes(styles.trackTitleContainer, styles.fadeToWhite, 'trackline-title-container')}
            data-test-trackline-title
          >
            <div className="trackline-catalogue-small">
              {track.is_stock_music ? (
                <button
                  className={styles.planLabel}
                  data-test-track-catalogue-type="paid"
                  onClick={() => this.setState({ showSubscriptionLabelInfo: true })}
                >
                  Included
                </button>
              ) : (
                <button
                  className={classes(
                    styles.planLabel,
                    'premium',
                    !trackIsAvailable && styles.unavailableTrackPlanColor
                  )}
                  data-test-track-catalogue-type="payg"
                  onClick={() => this.setState({ showSubscriptionLabelInfo: true })}
                >
                  Premium
                </button>
              )}
            </div>
            <p className={styles.trackTitle}>
              {track.is_explicit ? <ExplicitTag /> : null}

              <Tooltip
                className={classes(styles.boldFont, styles.customTooltipPosition)}
                positions={['top']}
                align={'start'}
                content={
                  <div>
                    <span>{track.title}</span>
                  </div>
                }
              >
                <Link
                  href={`/music/artists/${track.artists[0].slug}/track/${track.slug}`}
                  onClick={onClickLink}
                  className={!trackIsAvailable ? styles.unavailableTrackColor : undefined}
                  data-test-track-title
                >
                  {track.title}
                </Link>
              </Tooltip>
            </p>
            <div>
              <Tooltip
                className={classes(styles.customTooltipPosition)}
                positions={['top']}
                align={'start'}
                artists={track.artists}
                content={
                  <div>
                    {track.artists.map((artist, index) => (
                      <span key={index} style={{ display: 'inline', margin: '2px 0' }}>
                        {artist.name}
                        {index < track.artists.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </div>
                }
              >
                <ArtistLinkList
                  model={{ artists: track.artists }}
                  onClickLink={onClickLink}
                  linkClassName={classes(styles.artistLinkList, !trackIsAvailable && styles.unavailableTrackColor)}
                />
              </Tooltip>
            </div>
          </div>

          {/* VARIATIONS and NO VOCALS */}
          <div
            className={classes(styles.variations, 'trackline-variations-no-vocals')}
            data-test-trackline-variations-no-vocals
          >
            {/* Variations icon and tool tip */}
            {!mix && track.mixes_count > 0 && (
              <Tooltip
                className={classes(styles.variationToolTip)}
                positions={['top']}
                content={<span>See all versions</span>}
              >
                <div className={styles.variationContainer} onClick={this.handleClickVariation} data-test-variations>
                  <Link
                    href={`/music/artists/${track.artists[0].slug}/track/${track.slug}`}
                    onClick={onClickLink}
                    className={styles.variationsLink}
                  >
                    <Variations number={track.mixes_count} />
                  </Link>
                </div>
              </Tooltip>
            )}
            {/* No vocals icon and tool tip */}
            {track.is_instrumental && (
              <Tooltip
                className={classes(styles.variationToolTip)}
                wrapperClassName={styles.noVocals}
                positions={['top']}
                content={<span>No vocals</span>}
              >
                <NoVocal role="img" aria-label="This track is instrumental, no vocals" />
              </Tooltip>
            )}
          </div>

          {/* RELEASE */}
          <div className="trackline-release" data-test-trackline-release>
            {track.releases && track.releases.length > 0 && (
              <p className={styles.fadeToWhite}>
                {!isEmpty(track.releases[0]) && (
                  <Tooltip
                    className={classes(styles.customTooltipPosition)}
                    positions={['top']}
                    align={'start'}
                    content={<span>{track.releases[0].title}</span>}
                  >
                    <Link
                      href={`/music/artists/${track.releases[0].artist.slug}/release/${track.releases[0].slug}`}
                      className={!trackIsAvailable ? styles.unavailableTrackColor : undefined}
                      data-test-track-release
                    >
                      {track.releases[0].title}
                    </Link>
                  </Tooltip>
                )}
              </p>
            )}
          </div>

          {/* GENRE */}
          <div data-test-trackline-genre className="trackline-genre">
            {firstGenreTagName && (
              <Tooltip
                positions={['top']}
                content={
                  <span style={{ maxWidth: '12ch' }} data-test-track-line-genre-tool-tip>
                    {firstThreeGenreTags}
                  </span>
                }
                className={styles.toolTipStyles}
              >
                <TagButton
                  onClick={() => this.handleTagClick('genre', firstGenreTagSlug)}
                  text={firstGenreTagName}
                  loading={genreTagLoading}
                  tagStyle="small"
                  className={classes(styles.tagButton)}
                  data-test-track-line-genre-tag={firstGenreTagName}
                  aria-label={`Filter by genre: ${firstGenreTagName}`}
                />
              </Tooltip>
            )}
          </div>

          {/* MOOD */}
          <div data-test-trackline-mood className="trackline-mood">
            {firstMoodTagName && (
              <Tooltip
                positions={['top']}
                content={
                  <span style={{ maxWidth: '12ch' }} data-test-track-line-genre-tool-tip>
                    {firstThreeMoodTags}
                  </span>
                }
                className={styles.toolTipStyles}
              >
                <TagButton
                  onClick={() => this.handleTagClick('mood', firstMoodTagSlug)}
                  text={firstMoodTagName}
                  loading={moodTagLoading}
                  tagStyle="small"
                  className={classes(styles.tagButton)}
                  data-test-track-line-mood-tag={firstMoodTagName}
                  aria-label={`Filter by mood: ${firstMoodTagName}`}
                />
              </Tooltip>
            )}
          </div>

          {/* DURATION */}
          <div
            className={classes(!trackIsAvailable && styles.unavailableTrackColor, 'trackline-duration')}
            data-test-track-duration
          >
            <Duration value={track.duration} />
          </div>

          {/* CATALOGUE TYPE */}
          <div className="trackline-catalogue-type" data-test-trackline-catalogue-type>
            {track.is_stock_music ? (
              <button
                className={classes(styles.planLabel)}
                data-test-track-catalogue-type="paid"
                onClick={() => this.setState({ showSubscriptionLabelInfo: true })}
              >
                <span className="trackline-catalogue-label">Included</span>
                <CheckmarkCircle color={rebrand.dark1.toString()} checkColor={colorWhite.toString()} size={15} />
              </button>
            ) : (
              <button
                className={classes(styles.planLabel, 'premium')}
                data-test-track-catalogue-type="payg"
                onClick={() => this.setState({ showSubscriptionLabelInfo: true })}
              >
                <span
                  className={classes(
                    'trackline-catalogue-label',
                    !trackIsAvailable && styles.unavailableTrackPlanColor
                  )}
                >
                  Premium
                </span>{' '}
                <StarCircle
                  color={!trackIsAvailable ? rebrand.primary[20].toString() : rebrand.primary[100].toHexString()}
                  size={13}
                />
              </button>
            )}
          </div>

          {/* ACTION BUTTONS */}
          <div className={styles.actionButtons} data-test-trackline-actions>
            {trackIsAvailable && (
              <>
                <AddRemoveCompat track={track} size={32} />
                <FavouriteButtonCompat track={track} size={32} className="trackline-favourite-btn" />
              </>
            )}

            {!trackIsAvailable && (
              <>
                <Tooltip
                  className={classes(styles.tooltipCloseCross)}
                  positions={['top']}
                  show={this.state.showUnavailableTooltip}
                  disableOnMobile={false}
                  onClickOutside={() => this.setState({ showUnavailableTooltip: false })}
                  content={unavailableTooltipContent}
                >
                  <button
                    className={tooltipStyles.tooltipBtn}
                    onClick={() =>
                      this.setState((state) => ({ showUnavailableTooltip: !state.showUnavailableTooltip }))
                    }
                  >
                    <InfoOutline />
                  </button>
                </Tooltip>

                {/* This is intentionally commented, comment will be removed when notify api is ready */}
                {/* <NotifyButton /> */}
              </>
            )}

            {/* Add/remove from playlist buttons */}
            <UserPlaylistContext.Consumer>
              {(playlist) => {
                const userOwnsPlaylist = playlist && playlist.user_uuid && user && user.identity === playlist.user_uuid;

                if (trackIsAvailable) {
                  if (userOwnsPlaylist) {
                    // User owns the playlist, show the RemoveFromPlaylistButton
                    return (
                      <RemoveFromPlaylistButton track={track} playlist={playlist} onClick={onRemoveTrackFromPlaylist} />
                    );
                  } else {
                    // Show the AddToPlaylistButton
                    return (
                      <AddToPlaylistButton
                        className={styles.addToPlaylistButton}
                        track={track}
                        onAddToPlaylist={onAddTrackToPlaylist}
                        onTriggerSubscribePlaylistModal={() => showPlaylistSubscribeModal()}
                      />
                    );
                  }
                } else {
                  return null;
                }
              }}
            </UserPlaylistContext.Consumer>

            <TrackInfoButton track={track} />
          </div>
        </div>
      </div>
    );
  }

  handleClickVariation = (event: React.MouseEvent<any>): void => {
    this.props.controller.analytics.sendMixpanel('user clicks link variation');
  };

  handleTagClick = (filterType: 'genre' | 'mood', tagSlug: string) => {
    const {
      model: { router },
      controller: {
        analytics: { sendMixpanel },
        page: {
          searchMvp: { updateMoodOrGenre },
        },
      },
    } = this.props;

    sendMixpanel(`User clicks trackline ${filterType} tag`, { tag: tagSlug });

    const isSearchPage = router.location.pathname.startsWith('/search');

    if (isSearchPage) {
      // Refine the search filters
      updateMoodOrGenre(filterType, tagSlug, false);
    } else {
      // Navigate to the search page with the new set of params
      router.push(`/search?${filterType}=${tagSlug}`);
    }
  };

  rearrangeTags(tags: Array<{ name: string; slug: string }>, searchTags: string[]) {
    const tagsCopy = [...tags]; // Copy the tags array to avoid mutating the original

    searchTags.forEach((searchTag) => {
      const index = tagsCopy.findIndex((tag) => tag.slug === searchTag);
      // If a matching tag is found
      if (index > -1) {
        // Move the matching tag to the front
        const [matchingTag] = tagsCopy.splice(index, 1);
        tagsCopy.unshift(matchingTag);
      }
    });

    return tagsCopy;
  }
}
